import {Box, Grid, Typography} from '@mui/material';
import {ExamModule, StandardSubject} from '@cp/model';
import {env} from '../../config/env';
import StudyModuleCard from './CoursesAvailableGroupCard';

export interface CoursesAvailableGroupProps {
  subject: StandardSubject;
  levels: Record<string, Record<string, ExamModule[]>>;
  onModuleSelect: (module: ExamModule) => void;
}

export default function CoursesAvailableGroup({subject, levels, onModuleSelect}: CoursesAvailableGroupProps) {
  const subjectColor = {bg: env.subjects[subject].bg, color: env.subjects[subject].color};
  const subjectLabel = subject
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase());

  // Flatten all modules from all levels and exam boards
  const allModules: ExamModule[] = [];
  Object.values(levels).forEach((examBoards) => {
    Object.values(examBoards).forEach((modules) => {
      allModules.push(...modules);
    });
  });

  // Group by exam board if there are multiple exam boards
  const modulesByExamBoard = allModules.reduce(
    (acc, module) => {
      const examBoard = module.examBoard || 'No Exam Board';
      if (!acc[examBoard]) {
        acc[examBoard] = [];
      }
      acc[examBoard].push(module);
      return acc;
    },
    {} as Record<string, ExamModule[]>,
  );

  const hasMultipleExamBoards = Object.keys(modulesByExamBoard).length > 1;

  return (
    <Box sx={{mb: 4}}>
      <Box
        sx={{
          bgcolor: subjectColor.bg,
          color: subjectColor.color,
          p: 2,
          borderRadius: 2,
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{fontWeight: 600}}>
          {subjectLabel}
        </Typography>
      </Box>

      {Object.entries(modulesByExamBoard).map(([examBoard, modules]) => (
        <Box key={examBoard} sx={{mb: 3}}>
          {hasMultipleExamBoards && examBoard !== 'No Exam Board' && (
            <Typography variant="body2" color="text.secondary" sx={{mb: 2, fontWeight: 500}}>
              {examBoard}
            </Typography>
          )}
          <Grid container spacing={2}>
            {modules.map((module) => (
              <Grid size={{xs: 12, sm: 6, md: 4}} key={module.id}>
                <StudyModuleCard module={module} onSelect={onModuleSelect} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
}
